/**
 * Offline Authentication Store
 * Manages authentication state for offline scenarios
 * Requirements: 9.6, 9.7
 */

import { create } from 'zustand';
import { validateTokenOffline } from '../utils/offlineTokenValidator';
import { securityAuditLogger } from '../utils/securityAuditLogger';

interface OfflineAuthState {
  isOnline: boolean;
  hasValidOfflineToken: boolean;
  offlineUserId: string | null;
  offlineUserEmail: string | null;
  tokenExpiresAt: Date | null;
  lastTokenCheck: Date | null;
  
  // Actions
  checkOfflineAuth: () => void;
  setOnlineStatus: (isOnline: boolean) => void;
  clearOfflineAuth: () => void;
  refreshOfflineTokenStatus: () => void;
}

export const useOfflineAuthStore = create<OfflineAuthState>((set, get) => ({
  isOnline: navigator.onLine,
  hasValidOfflineToken: false,
  offlineUserId: null,
  offlineUserEmail: null,
  tokenExpiresAt: null,
  lastTokenCheck: null,

  checkOfflineAuth: () => {
    const token = localStorage.getItem('jwt_token');
    const validation = validateTokenOffline(token);
    
    const now = new Date();
    
    if (validation.isValid && validation.payload) {
      set({
        hasValidOfflineToken: true,
        offlineUserId: validation.payload.userId,
        offlineUserEmail: validation.payload.email,
        tokenExpiresAt: new Date(validation.payload.exp * 1000),
        lastTokenCheck: now
      });
      
      // Log successful offline auth check
      securityAuditLogger.logSecurityEvent('offline_auth_check_success', {
        userId: validation.payload.userId,
        email: validation.payload.email,
        expiresAt: new Date(validation.payload.exp * 1000).toISOString(),
        timestamp: now.toISOString()
      });
    } else {
      set({
        hasValidOfflineToken: false,
        offlineUserId: null,
        offlineUserEmail: null,
        tokenExpiresAt: null,
        lastTokenCheck: now
      });
      
      // Log failed offline auth check
      securityAuditLogger.logSecurityEvent('offline_auth_check_failure', {
        error: validation.error || 'Token validation failed',
        isExpired: validation.isExpired,
        timestamp: now.toISOString()
      });
    }
  },

  setOnlineStatus: (isOnline: boolean) => {
    const currentState = get();
    
    set({ isOnline });
    
    // Log online status change
    securityAuditLogger.logSecurityEvent('online_status_change', {
      isOnline,
      previousStatus: currentState.isOnline,
      timestamp: new Date().toISOString()
    });
    
    // If going online, we might want to refresh auth status
    if (isOnline && !currentState.isOnline) {
      // Trigger a fresh auth check when coming back online
      // This will be handled by the main auth store
      securityAuditLogger.logSecurityEvent('connection_restored', {
        hadValidOfflineToken: currentState.hasValidOfflineToken,
        timestamp: new Date().toISOString()
      });
    }
  },

  clearOfflineAuth: () => {
    const currentState = get();
    
    set({
      hasValidOfflineToken: false,
      offlineUserId: null,
      offlineUserEmail: null,
      tokenExpiresAt: null,
      lastTokenCheck: new Date()
    });
    
    // Log offline auth clearance
    securityAuditLogger.logSecurityEvent('offline_auth_cleared', {
      previousUserId: currentState.offlineUserId,
      timestamp: new Date().toISOString()
    });
  },

  refreshOfflineTokenStatus: () => {
    // Re-check the current token status
    get().checkOfflineAuth();
  }
}));

// Set up online/offline event listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useOfflineAuthStore.getState().setOnlineStatus(true);
  });

  window.addEventListener('offline', () => {
    useOfflineAuthStore.getState().setOnlineStatus(false);
  });

  // Initial check
  useOfflineAuthStore.getState().checkOfflineAuth();
}

// Helper functions for components
export const offlineAuthHelpers = {
  /**
   * Check if user can access reading history offline
   */
  canAccessReadingHistoryOffline: (): boolean => {
    const state = useOfflineAuthStore.getState();
    return !state.isOnline && state.hasValidOfflineToken;
  },

  /**
   * Check if user should be redirected to login
   */
  shouldRedirectToLogin: (): boolean => {
    const state = useOfflineAuthStore.getState();
    return !state.isOnline && !state.hasValidOfflineToken;
  },

  /**
   * Get offline user info
   */
  getOfflineUserInfo: (): { userId: string | null; email: string | null } => {
    const state = useOfflineAuthStore.getState();
    return {
      userId: state.offlineUserId,
      email: state.offlineUserEmail
    };
  },

  /**
   * Get token expiration info for display
   */
  getTokenExpirationInfo: (): { expiresAt: Date | null; isExpiringSoon: boolean } => {
    const state = useOfflineAuthStore.getState();
    const expiresAt = state.tokenExpiresAt;
    
    if (!expiresAt) {
      return { expiresAt: null, isExpiringSoon: false };
    }
    
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    const isExpiringSoon = expiresAt <= fiveMinutesFromNow;
    
    return { expiresAt, isExpiringSoon };
  },

  /**
   * Format offline auth status for display
   */
  getOfflineAuthStatusMessage: (): string => {
    const state = useOfflineAuthStore.getState();
    
    if (state.isOnline) {
      return 'Online - Full access available';
    }
    
    if (state.hasValidOfflineToken) {
      const { expiresAt, isExpiringSoon } = offlineAuthHelpers.getTokenExpirationInfo();
      if (isExpiringSoon && expiresAt) {
        const minutes = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60));
        return `Offline - Access expires in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      return 'Offline - Cached content available';
    }
    
    return 'Offline - Login required for access';
  }
};